<template>
  <div
    v-show="isFiltered"
    ref="item"
    class="item-wrapper"
    :class="itemClasses"
    @mouseover="hover"
    @click="toggle"
  >
    <div
      class="item-container"
      :class="{ multiselect: isMultiselect }"
    >
      <span v-if="isMultiselect && isCheckboxVisible">
        <NorticCheckbox
          :is-checked="isSelected"
          tabindex="-1"
        />
      </span>
      <span>
        <slot>
          {{ calcLabel }}
        </slot>
      </span>
    </div>
  </div>
</template>

<script>
import NorticCheckbox from '@/components/common/input/selection/checkbox/nortic-checkbox/NorticCheckbox.vue';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import union from 'lodash/union';

export default {
  name: 'NorticSelectItem',
  components: { NorticCheckbox },
  props: {
    value: {
      type: [Number, String, Array, Object, Boolean],
      default: null,
    },
    label: {
      type: [String, Number],
      default: null,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isPreSelected: {
      type: Boolean,
      default: false,
    },
    isGroup: {
      type: Boolean,
      default: false,
    },
    isCheckboxVisible: {
      type: Boolean,
      default: true,
    },
    isEqualFunction: {
      type: Function,
      default: isEqual,
    },
  },
  data() {
    return {
      isScrollable: true,
      index: null,
    };
  },
  computed: {
    isIncludedInParentValue() {
      return this.$parent.innerValue.some((value) => this.isEqualFunction(this.value, value));
    },
    isSelected() {
      if (!this.isMultiselect) {
        return this.value === this.$parent.innerValue;
      }

      if (this.isGroup && isArray(this.value)) {
        return this.value.every((arrValue) => this.$parent.innerValue.some((value) => this.isEqualFunction(arrValue, value)));
      }

      return this.isIncludedInParentValue;
    },
    isHighlighted() {
      return isEqual(this.itemAsObject, this.$parent.highlightedItem);
    },
    itemClasses() {
      return {
        'is-selected': this.isSelected,
        'is-highlighted': this.isHighlighted,
        'is-disabled': this.isDisabled,
      };
    },
    isFiltered() {
      return this.$parent.filter(this.$parent.filterValue, this.value, this.calcLabel);
    },
    itemAsObject() {
      return {
        value: this.value,
        label: this.calcLabel,
        isSelected: this.isSelected,
        isDisabled: this.isDisabled,
        isFiltered: this.isFiltered,
        isGroup: this.isGroup,
      };
    },
    calcLabel() {
      if (this.label === null) {
        return this.value
          ? `${this.value}`
          : null;
      }

      return this.label;
    },
    isMultiselect() {
      return this.$parent.isMultiselect;
    },
  },
  watch: {
    isHighlighted(value) {
      if (value && this.isScrollable) {
        this.$scrollTo(this.$refs.item, 1, {
          container: this.$parent.$refs.scrollList,
          force: false,
        });
      }

      this.isScrollable = true;
    },
    'itemAsObject.value': {
      handler(value) {
        if (this.$parent.items[this.index]) {
          this.$set(this.$parent.items[this.index], 'value', value);
        }
      },
    },
    'itemAsObject.label': {
      handler(value) {
        if (this.$parent.items[this.index]) {
          this.$set(this.$parent.items[this.index], 'label', value);
        }
      },
    },
    'itemAsObject.isSelected': {
      handler(value) {
        if (this.$parent.items[this.index]) {
          this.$set(this.$parent.items[this.index], 'isSelected', value);
        }
      },
    },
    'itemAsObject.isDisabled': {
      handler(value) {
        if (this.$parent.items[this.index]) {
          this.$set(this.$parent.items[this.index], 'isDisabled', value);
        }
      },
    },
    'itemAsObject.isFiltered': {
      handler(value) {
        if (this.$parent.items[this.index]) {
          this.$set(this.$parent.items[this.index], 'isFiltered', value);
        }
      },
    },
    'itemAsObject.isGroup': {
      handler(value) {
        if (this.$parent.items[this.index]) {
          this.$set(this.$parent.items[this.index], 'isGroup', value);
        }
      },
    },
    '$parent.items.length': {
      handler() {
        const index = this.$parent.items.findIndex((item) => item.value === this.value && item.label === this.label);
        if (index > -1 && index !== this.index) {
          this.index = index;
        }
      },
    },
  },
  mounted() {
    this.index = this.$parent.items.push(this.itemAsObject) - 1;

    if (this.isPreSelected) {
      this.$parent.innerValue = this.value;
    }
  },
  beforeDestroy() {
    this.$delete(this.$parent.items, this.index);
  },
  methods: {
    hover() {
      if (!this.isDisabled) {
        this.isScrollable = false;
        this.$parent.highlightedItem = this.itemAsObject;
      }
    },
    select() {
      if (!this.isDisabled) {
        if (!this.isMultiselect) {
          this.$parent.innerValue = this.value;
          this.$parent.highlightedItem = this.itemAsObject;
          this.$parent.close();
        } else {
          if (this.isGroup && isArray(this.value)) {
            this.$parent.innerValue = union(this.value, this.$parent.innerValue);
          } else if (!this.isIncludedInParentValue) {
            this.$parent.innerValue.push(this.value);
          }

          this.$parent.$emit('checked', this.itemAsObject);
        }
      }
    },
    unselect() {
      if (!this.isMultiselect) {
        this.$parent.innerValue = null;
        this.$parent.highlightedItem = this.itemAsObject;
        this.$parent.close();
      } else {
        if (this.isGroup && isArray(this.value)) {
          this.$parent.innerValue = this.$parent.innerValue.filter((val) => this.value.every((value) => !this.isEqualFunction(value, val)));
        } else if (this.isIncludedInParentValue) {
          this.$parent.innerValue = this.$parent.innerValue.filter((val) => !this.isEqualFunction(val, this.value));
          this.$parent.highlightedItem = this.itemAsObject;
        }

        this.$parent.$emit('unchecked', this.itemAsObject);
      }
    },
    toggle() {
      if (this.isSelected && this.isMultiselect) {
        this.unselect();
      } else {
        this.select();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .item-wrapper {
    &:not(:last-child) {
      .item-container {
        border-bottom: 1px solid var(--border-color-hex);
      }
    }

    &.is-highlighted {
      background: var(--input-hover-bg-hex);
    }

    &.is-selected {
      background: var(--input-active-bg-hex);
      color: var(--primary-text-alt-color-hex);
    }

    &.is-disabled {
      color: rgba(var(--primary-variant-text-rgb), 0.3);
      background: rgba(var(--primary-variant-bg-rgb), 0.3);
      cursor: default;
    }

    cursor: pointer;
    padding: 0 10px;
    color: var(--primary-text-hex);
    background: var(--primary-bg-hex);

    .item-container {
      &.multiselect {
        grid-template-columns: auto 1fr;
        display: grid;
        grid-gap: 10px;
        align-items: center;
      }

      padding: 10px 0;
    }
  }
</style>
